import React, { useContext, useEffect, useState } from 'react';
import { Table, Modal, Form } from 'react-bootstrap';
import { ImageFile, ImagesResponse, OrderDirection } from '../../Models/Models';
import { FieldLengths } from '../../Models/FieldLengths';
import { SiteContext } from '../../Contexts';
import { EntityColors } from '../../Themes/EntityColors';
import ImageDeleteModal from '../Modals/ImageDeleteModal';
import { ImageService } from '../../Services/ImageService';
import SaveButton from '../Buttons/SaveButton';
import DiscardButton from '../Buttons/DiscardButton';
import DeleteButton from '../Buttons/DeleteButton';
import EditButton from '../Buttons/EditButton';
import { Utils } from '../../Services/Utils';
import TableShell from '../Table/TableShell';
import TableToolbar from '../Table/TableToolbar';
import UserAvatarCell from '../Avatar/UserAvatarCell';
import CharCount from '../Form/CharCount';

const ImageTableComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle('Obrázky');
    }, []);

    const { filterImages, updateImage, deleteImage } = ImageService();
    const { currentSite }: any = useContext(SiteContext);

    const [response, setResponse] = useState<ImagesResponse | null>(null);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>('Desc');

    const [showPreview, setShowPreview] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
    const [editMode, setEditMode] = useState(false);
    const [initialAlt, setInitialAlt] = useState('');
    const [altText, setAltText] = useState('');
    const [idToDelete, setIdToDelete] = useState(0);

    const { action, icon } = EntityColors.image;

    const loadPage = async (pageNum: number, opts?: { perPage?: number; order?: OrderDirection }) => {
        const limit = opts?.perPage ?? perPage;
        if (currentSite.id === -1) return;
        const data = await filterImages({
            websiteId: currentSite.id,
            limit,
            offset: (pageNum - 1) * limit,
            orderDirection: opts?.order ?? orderDirection,
        });
        setResponse(data ?? null);
    };

    const changePerPage = (count: number) => {
        setPerPage(count);
        setPage(1);
        void loadPage(1, { perPage: count });
    };

    const changeOrder = (dir: OrderDirection) => {
        setOrderDirection(dir);
        setPage(1);
        void loadPage(1, { order: dir });
    };

    useEffect(() => {
        setPage(1);
        void loadPage(1);
    }, [currentSite]);

    const handleClick = (image: ImageFile) => {
        setSelectedImage(image);
        setInitialAlt(image.alt);
        setAltText(image.alt);
        setShowPreview(true);
    };

    const closePreview = () => {
        setShowPreview(false);
        setEditMode(false);
    };

    const closeAll = () => {
        closePreview();
        setShowDelete(false);
        setIdToDelete(0);
    };

    const saveAlt = () => {
        if (!selectedImage) return;
        updateImage(selectedImage.id, altText)
            .then(() => { closeAll(); loadPage(page); })
            .catch(closeAll);
    };

    const confirmDelete = () => {
        if (!selectedImage) return;
        deleteImage(selectedImage.id)
            .then(() => { closeAll(); loadPage(1); setPage(1); })
            .catch(closeAll);
    };

    const startDelete = () => {
        if (!selectedImage) return;
        setIdToDelete(selectedImage.id);
        setShowDelete(true);
    };

    return (
        <>
            <TableShell
                title="Obrázky"
                icon={icon}
                accentColor={action}
                createHref="/image/add"
                createLabel="Nahrať"
                isLoading={response === null}
                isEmpty={response !== null && response.uploadedImages.length === 0}
                emptyMessage="Žiadne obrázky"
                page={page}
                totalCount={response?.totalCount}
                perPage={perPage}
                onPageChange={p => { setPage(p); loadPage(p); }}
                toolbar={
                    <TableToolbar
                        totalCount={response?.totalCount ?? 0}
                        nounForms={['obrázok', 'obrázky', 'obrázkov']}
                        perPage={perPage}
                        onPerPageChange={changePerPage}
                        pageSizes={[12, 24, 48, 96]}
                        orderDirection={orderDirection}
                        onOrderChange={changeOrder}
                    />
                }
            >
                <Table responsive>
                    <thead>
                        <tr>
                            <th>Náhľad</th>
                            <th>Popis</th>
                            <th>Vytvorené</th>
                            <th>Vytvoril</th>
                            <th>Naposledy upravené</th>
                            <th>Upravil</th>
                            <th className="text-center">Akcie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {response?.uploadedImages.map(image => (
                            <tr key={image.id}>
                                <td>
                                    <img
                                        src={image.filePath.replace('original.jpg', '180x120.webp')}
                                        alt={image.alt}
                                        className="img-thumbnail st-image-table-thumb"
                                        onClick={() => handleClick(image)}
                                    />
                                </td>
                                <td>{image.alt || <span className="st-cell-dash">–</span>}</td>
                                <td className="text-nowrap">
                                    {image.createdAt ? Utils.formatDate(image.createdAt) : '—'}
                                </td>
                                <td>
                                    <UserAvatarCell userId={image.createdById} />
                                </td>
                                <td className="text-nowrap st-cell-soft">
                                    {image.modifiedAt ? Utils.formatDate(image.modifiedAt) : '—'}
                                </td>
                                <td>
                                    <UserAvatarCell userId={image.modifiedById} />
                                </td>
                                <td className="text-center">
                                    <div className="st-row-actions">
                                        <EditButton color={action} iconOnly onClick={() => handleClick(image)} />
                                        <DeleteButton
                                            color={action}
                                            iconOnly
                                            onClick={() => { setSelectedImage(image); setIdToDelete(image.id); setShowDelete(true); }}
                                        />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </TableShell>

            <Form>
                <Modal show={showPreview && !showDelete} onHide={closePreview} size="lg">
                    <Modal.Header
                        closeButton
                        className="st-preview-head"
                        style={{ '--head-tint': EntityColors.image.background, '--head-accent': action } as React.CSSProperties}
                    >
                        <Modal.Title>Náhľad obrázka</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {selectedImage && (
                            <>
                                <img
                                    src={selectedImage.filePath}
                                    alt={selectedImage.alt}
                                    className="img-fluid mb-3 st-crop-preview-img"
                                />
                                {editMode ? (
                                    <Form.Group>
                                        <div className="st-field-head">
                                            <Form.Label>Popis obrázka</Form.Label>
                                            <CharCount value={altText} max={FieldLengths.imageAlt} />
                                        </div>
                                        <Form.Control
                                            type="text"
                                            maxLength={FieldLengths.imageAlt}
                                            value={altText}
                                            required
                                            onChange={e => setAltText(e.target.value)}
                                            autoFocus
                                        />
                                    </Form.Group>
                                ) : (
                                    <p><b>Popis obrázka:</b> {selectedImage.alt}</p>
                                )}
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        {editMode ? (
                            <div className="d-flex gap-2">
                                <SaveButton onClick={saveAlt} />
                                <DiscardButton onClick={() => { setEditMode(false); setAltText(initialAlt); }} />
                            </div>
                        ) : (
                            <div className="d-flex gap-2">
                                <DeleteButton onClick={startDelete} />
                                <EditButton onClick={() => setEditMode(true)} color="#0d6efd" text="Upraviť popis" />
                            </div>
                        )}
                    </Modal.Footer>
                </Modal>
            </Form>

            <ImageDeleteModal show={showDelete} onHide={closeAll} onSubmit={confirmDelete} idToDelete={idToDelete} />
        </>
    );
};

export default ImageTableComponent;
